/**
 * Partner Dashboard Page
 * Restricted view for partner users showing only redemption analytics for their locations
 */

import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { getUserLocations } from "../services/locations.service";
import {
  getRedemptionsPaginated,
  type EnrichedVoucher,
} from "../services/redemptions.service";
import { QUERY_KEYS } from "../utils/constants";
import { formatCurrency, formatDate } from "../utils/formatters";
import Card from "../components/ui/Card";
import LoadingSpinner from "../components/common/LoadingSpinner";

/**
 * Partner Dashboard Component
 */
const PartnerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<
    "day" | "week" | "month" | "custom"
  >("week");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [sortField, _setSortField] = useState<"date" | "value" | "code">(
    "date",
  );
  const [sortDirection, _setSortDirection] = useState<"asc" | "desc">("desc");

  // Fetch user's assigned locations
  const { data: userLocations = [] } = useQuery({
    queryKey: [QUERY_KEYS.LOCATIONS, "user", user?.id],
    queryFn: () => getUserLocations(user!.id),
    enabled: !!user?.id,
  });

  // Calculate date range based on selected period
  useEffect(() => {
    if (selectedPeriod === "custom") {
      // Use custom date inputs
      setDateRange({
        from: customDateFrom || "",
        to: customDateTo || "",
      });
    } else {
      // Calculate preset period
      const today = new Date();
      const from = new Date();

      if (selectedPeriod === "day") {
        from.setDate(today.getDate() - 1);
      } else if (selectedPeriod === "week") {
        from.setDate(today.getDate() - 7);
      } else if (selectedPeriod === "month") {
        from.setMonth(today.getMonth() - 1);
      }

      setDateRange({
        from: from.toISOString().split("T")[0],
        to: today.toISOString().split("T")[0],
      });
    }
  }, [selectedPeriod, customDateFrom, customDateTo]);

  // Fetch vouchers via the SERVER-SCOPED redemptions RPC (get_redemptions_page).
  // Scope is derived server-side from the caller's auth.uid() (get_partner_scope), so a partner
  // only ever receives vouchers for their own store(s) — not the client-supplied org list, which
  // can't be trusted. Cancelled/test are already suppressed for partners by the RPC.
  const { data: voucherData, isLoading } = useQuery({
    queryKey: [
      QUERY_KEYS.QRCODES,
      "partner-redemptions",
      user?.id,
      dateRange.from,
      dateRange.to,
      sortField,
      sortDirection,
    ],
    queryFn: () =>
      getRedemptionsPaginated(user?.id, user?.role, {
        pageSize: 500,
        dateFrom: dateRange.from || undefined,
        dateTo: dateRange.to || undefined,
        sortField: sortField === "value" ? "value" : "date",
        sortDirection,
      }),
    enabled: userLocations.length > 0,
  });

  // Calculate summary metrics
  const totalVouchers = voucherData?.totalCount || 0;
  const totalIssued =
    voucherData?.data.reduce(
      (sum: number, v: EnrichedVoucher) => sum + v.discount_value,
      0,
    ) || 0;
  const totalRedeemed =
    voucherData?.data
      .filter((v: EnrichedVoucher) => v.is_redeemed)
      .reduce((sum: number, v: EnrichedVoucher) => sum + v.discount_value, 0) ||
    0;

  // Export function
  const handleExport = () => {
    if (!voucherData?.data) {
      return;
    }

    const csv = [
      ["Date", "Code", "Discount Value", "Status", "Redeemed Date"].join(","),
      ...voucherData.data.map((v: EnrichedVoucher) =>
        [
          v.created_at ? formatDate(v.created_at) : "",
          v.voucher_code || "",
          v.discount_value || 0,
          v.is_redeemed ? "Redeemed" : "Active",
          v.redeemed_at ? formatDate(v.redeemed_at) : "",
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `voucher-report-${dateRange.from}-to-${dateRange.to}.csv`;
    link.click();
  };

  if (userLocations.length === 0 && !isLoading) {
    return (
      <div className="p-6">
        <Card padding="lg">
          <div className="text-center py-12">
            <p className="text-gray-500">
              No locations assigned to your account
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Contact your administrator to assign locations to view redemption
              data
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Get unique organizations from user locations
  const organizations = Array.from(
    new Set(userLocations.map((loc) => loc.organization_id)),
  ).map((orgId) => {
    return {
      id: orgId,
      name:
        userLocations.filter((loc) => loc.organization_id === orgId)[0]
          ?.organization?.name || "Unknown Organization",
    };
  });

  const locationNames = userLocations.map((loc) => loc.name).join(", ");

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Partner Dashboard</h1>
        <p className="text-gray-600 mt-1">
          View redemption analytics for your locations
        </p>

        {/* Organization & Location Info */}
        {userLocations.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Organization{organizations.length > 1 ? "s" : ""}:{" "}
                </span>
                <span className="text-sm text-gray-900">
                  {organizations.map((org) => org.name).join(", ")}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Locations ({userLocations.length}):{" "}
                </span>
                <span className="text-sm text-gray-900">
                  {locationNames || "All locations"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Period Selector */}
      <Card padding="md">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="text-sm font-medium text-gray-700">
            Time Period:
          </label>
          <div className="flex gap-2">
            {(["day", "week", "month", "custom"] as const).map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedPeriod === period
                    ? "bg-primary-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>

          {selectedPeriod === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customDateFrom}
                onChange={(e) => setCustomDateFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customDateTo}
                onChange={(e) => setCustomDateTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          )}

          <button
            onClick={handleExport}
            disabled={!voucherData?.data || voucherData.data.length === 0}
            className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
          >
            Export CSV
          </button>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card padding="md">
          <div className="text-sm text-gray-500">Total Vouchers</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">
            {totalVouchers}
          </div>
        </Card>
        <Card padding="md">
          <div className="text-sm text-gray-500">Total Issued</div>
          <div className="text-2xl font-bold text-green-600 mt-1">
            {formatCurrency(totalIssued)}
          </div>
        </Card>
        <Card padding="md">
          <div className="text-sm text-gray-500">Total Redeemed</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">
            {formatCurrency(totalRedeemed)}
          </div>
        </Card>
      </div>

      {/* Voucher Table */}
      <Card>
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="large" />
            </div>
          ) : voucherData?.data && voucherData.data.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Redeemed
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {voucherData.data.map((voucher: EnrichedVoucher) => (
                  <tr key={voucher.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {voucher.voucher_code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(voucher.discount_value)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          voucher.is_redeemed
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {voucher.is_redeemed ? "Redeemed" : "Active"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {voucher.created_at
                        ? formatDate(voucher.created_at)
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {voucher.redeemed_at
                        ? formatDate(voucher.redeemed_at)
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">
                No voucher data for selected period
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PartnerDashboard;
